/**
 * ServiceNow Host Bridge Implementation
 *
 * Handles bidirectional communication between the Angular app and ServiceNow portal
 * using the postMessage API.
 */

import { Injectable } from '@angular/core';
import {
  HostBridge,
  HostMessage,
  UserContext,
  AclCheckRequest,
  AclCheckResponse,
  OutgoingMessageTypes,
} from './host-bridge.interface';
import { environment } from '../../../environments/environment';

/**
 * Allowed origins for ServiceNow postMessage communication.
 * Add your ServiceNow instance URLs here.
 */
const SERVICENOW_ALLOWED_ORIGINS = [
  'https://ven07325.service-now.com',
  'https://*.service-now.com',
  'https://localhost:4200',
];

interface PendingRequest {
  resolve: (value: any) => void;
  reject: (reason: any) => void;
  timeoutId: ReturnType<typeof setTimeout>;
}

@Injectable()
export class ServiceNowHostBridge extends HostBridge {
  private readonly REQUEST_TIMEOUT_MS = 10000; // 10 seconds
  private messageHandlers = new Map<string, Set<(payload: any) => void>>();
  private pendingRequests = new Map<string, PendingRequest>();
  private requestIdCounter = 0;
  private messageListener: ((event: MessageEvent) => void) | null = null;
  private isInitialized = false;

  constructor() {
    super();
    this.initializeMessageListener();
  }

  /**
   * Check if the app is running inside an iframe
   */
  isEmbedded(): boolean {
    try {
      return window.self !== window.top;
    } catch {
      // If we can't access window.top due to cross-origin restrictions,
      // we're definitely in an iframe
      return true;
    }
  }

  /**
   * Request user context from ServiceNow
   */
  async getUserContext(): Promise<UserContext> {
    if (!this.isEmbedded()) {
      throw new Error('Cannot get user context: not running in embedded mode');
    }

    return this.sendRequestAndWaitForResponse<UserContext>(
      OutgoingMessageTypes.GET_USER_CONTEXT,
      {}
    );
  }

  /**
   * Check ACL permissions with ServiceNow
   */
  async checkAcl(request: AclCheckRequest): Promise<AclCheckResponse> {
    if (!this.isEmbedded()) {
      // In standalone mode (should not happen if using factory correctly)
      return { allowed: true };
    }

    return this.sendRequestAndWaitForResponse<AclCheckResponse>(
      OutgoingMessageTypes.CHECK_ACL,
      request
    );
  }

  /**
   * Send a one-way message to ServiceNow (no response expected)
   */
  sendMessage(type: string, payload: any): void {
    if (!this.isEmbedded()) {
      console.warn('[ServiceNowHostBridge] Cannot send message: not in embedded mode');
      return;
    }

    const message: HostMessage = {
      type,
      payload,
      timestamp: Date.now(),
    };

    this.postToParent(message);
  }

  /**
   * Register a handler for incoming messages from ServiceNow
   */
  onMessage<T>(type: string, handler: (payload: T) => void): void {
    if (!this.messageHandlers.has(type)) {
      this.messageHandlers.set(type, new Set());
    }
    this.messageHandlers.get(type)!.add(handler);
  }

  /**
   * Remove a message handler
   */
  offMessage<T>(type: string, handler: (payload: T) => void): void {
    const handlers = this.messageHandlers.get(type);
    if (handlers) {
      handlers.delete(handler);
      if (handlers.size === 0) {
        this.messageHandlers.delete(type);
      }
    }
  }

  /**
   * Clean up resources
   */
  destroy(): void {
    // Remove message listener
    if (this.messageListener) {
      window.removeEventListener('message', this.messageListener);
      this.messageListener = null;
    }

    // Clear all pending requests with rejection
    this.pendingRequests.forEach((request, requestId) => {
      clearTimeout(request.timeoutId);
      request.reject(new Error('Host bridge destroyed'));
    });
    this.pendingRequests.clear();

    // Clear all message handlers
    this.messageHandlers.clear();

    this.isInitialized = false;
  }

  /**
   * Initialize the message event listener
   */
  private initializeMessageListener(): void {
    if (this.isInitialized) {
      return;
    }

    this.messageListener = (event: MessageEvent) => {
      this.handleIncomingMessage(event);
    };

    window.addEventListener('message', this.messageListener);
    this.isInitialized = true;

    console.log('[ServiceNowHostBridge] Message listener initialized');
  }

  /**
   * Handle incoming postMessage events
   */
  private handleIncomingMessage(event: MessageEvent): void {
    // Validate origin
    if (!this.isOriginAllowed(event.origin)) {
      console.warn('[ServiceNowHostBridge] Rejected message from unauthorized origin:', event.origin);
      return;
    }

    const message = event.data as HostMessage;

    // Validate message structure
    if (!message || typeof message !== 'object' || !message.type) {
      return;
    }

    console.log('[ServiceNowHostBridge] Received message:', message.type, message);

    // Check if this is a response to a pending request
    if (message.requestId && this.pendingRequests.has(message.requestId)) {
      const pendingRequest = this.pendingRequests.get(message.requestId)!;
      this.pendingRequests.delete(message.requestId);
      clearTimeout(pendingRequest.timeoutId);
      pendingRequest.resolve(message.payload);
      return;
    }

    // Dispatch to registered handlers
    const handlers = this.messageHandlers.get(message.type);
    if (handlers) {
      handlers.forEach((handler) => {
        try {
          handler(message.payload);
        } catch (error) {
          console.error(`[ServiceNowHostBridge] Error in message handler for ${message.type}:`, error);
        }
      });
    }
  }

  /**
   * Check if the message origin is in the allowed list
   */
  private isOriginAllowed(origin: string): boolean {
    // In development, allow localhost and null origin (file:// protocol for test harness)
    if (!environment.production) {
      if (origin === 'null' || origin.includes('localhost') || origin.includes('127.0.0.1')) {
        return true;
      }
    }

    return SERVICENOW_ALLOWED_ORIGINS.some((allowed: string) => {
      if (allowed.includes('*')) {
        // Convert wildcard pattern to regex
        const regex = new RegExp('^' + allowed.replace(/\./g, '\\.').replace(/\*/g, '.*') + '$');
        return regex.test(origin);
      }
      return origin === allowed;
    });
  }

  /**
   * Send a request and wait for a response
   */
  private sendRequestAndWaitForResponse<T>(type: string, payload: any): Promise<T> {
    return new Promise<T>((resolve, reject) => {
      const requestId = this.generateRequestId();

      // Set up timeout
      const timeoutId = setTimeout(() => {
        if (this.pendingRequests.has(requestId)) {
          this.pendingRequests.delete(requestId);
          reject(new Error(`Request timeout: ${type} (${requestId})`));
        }
      }, this.REQUEST_TIMEOUT_MS);

      // Store pending request
      this.pendingRequests.set(requestId, { resolve, reject, timeoutId });

      // Send the message
      const message: HostMessage = {
        type,
        payload,
        requestId,
        timestamp: Date.now(),
      };

      this.postToParent(message);
    });
  }

  /**
   * Post a message to the parent window
   */
  private postToParent(message: HostMessage): void {
    try {
      // Use '*' for target origin to allow communication during development
      // In production, the host should validate the iframe's origin
      window.parent.postMessage(message, '*');
      console.log('[ServiceNowHostBridge] Sent message:', message.type, message);
    } catch (error) {
      console.error('[ServiceNowHostBridge] Failed to post message:', error);
    }
  }

  /**
   * Generate a unique request ID
   */
  private generateRequestId(): string {
    return `req_${++this.requestIdCounter}_${Date.now()}`;
  }
}
